import React, { useCallback } from 'react';
import { useToolkitProject, ToolkitProjectHeader } from './toolkit/ToolkitProject';
import ToolkitFileStep from './toolkit/ToolkitFileStep';
import ToolkitBatchTable from './toolkit/ToolkitBatchTable';
import useToolkitBatch, { sourceRow } from './toolkit/useToolkitBatch';
import { useToolkitConfig, ToolkitConfigStep, missingConfig } from './toolkit/ToolkitConfig';
import './OhipTransactionsPage.css';
import './ToolkitBlocksPage.css';

// Sync › project › Toolkit › Deposit Ledger.
//
// Ported from the desktop toolkit's OHIP › Deposit Ledger: each deposit in an Opera v5 deposit ledger export
// (delimited) is posted to its Opera Cloud reservation as a DEPOSIT PAYMENT, through the project's own connection
// (toolkit ledgers.js post-deposit). The reservation is found by its v5 confirmation number under the project
// connector's external system. A row is skipped — and says why — when the reservation is missing, cancelled, no-show,
// checked in or out, when its dates differ from Opera Cloud's, or when the same deposit (amount and payment method) is
// already on its deposit folio. A date mismatch can be posted anyway, row by row, once checked.

const COLUMNS = [
    { key: 'CONFIRMATION_NO', label: 'Confirmation' },
    { key: 'NAME', label: 'Guest' },
    { key: 'ARRIVAL', label: 'Arrival' },
    { key: 'DEPARTURE', label: 'Departure' },
    { key: 'RESV_STATUS', label: 'v5 status' },
    { key: 'TOTAL_PAID_AMT', label: 'Deposit (v5)' },
    { key: 'postedAmount', label: 'Posted' },
];
const SEARCH_KEYS = ['CONFIRMATION_NO', 'NAME', 'RESV_STATUS'];
const ROW_ACTIONS = [{ label: 'Post anyway', title: 'Post despite the date mismatch', show: (r) => r.dateMismatch, opts: { bypassDateCheck: true } }];


function ToolkitDepositLedgerPage() {
    const toolkit = useToolkitProject();
    const { ctx, ready } = toolkit;
    const config = useToolkitConfig(ctx, { paymentMethods: true });
    const canPost = ready && !missingConfig(config, [!config.paymentMethod && 'a payment method']).length;

    // On the server a row is never posted past a date mismatch: "Post anyway" is the row's own button, here.
    const request = useCallback((row, opts = {}) => ({ area: 'ledger', op: 'post-deposit', args: {
        v5Record: sourceRow(row, ['postedAmount', 'dateMismatch']),
        paymentMethod: config.paymentMethod, currencyCode: config.currencyCode, cashierId: config.cashierId,
        amountOverride: config.convertAmount(row.TOTAL_PAID_AMT),
        bypassDateCheck: !!opts.bypassDateCheck,
    } }), [config]);
    const interpret = useCallback((r, row) => ({ status: r.status, errorMessage: r.errorMessage || null, dateMismatch: !!r.dateMismatch,
        postedAmount: r.postedAmount ? `${r.postedAmount} ${r.currencyCode}` : row.postedAmount }), []);

    const batch = useToolkitBatch({ request, interpret, ctx,
        server: { screen: 'deposit-ledger', title: 'Deposit Ledger', keyOf: (row) => row.CONFIRMATION_NO } });
    const missing = missingConfig(config, [!config.paymentMethod && 'a payment method']);

    return (
        <div className="otx-page">
            <ToolkitProjectHeader toolkit={toolkit} title="Deposit Ledger"
                subtitle="Post the deposits of an Opera v5 deposit ledger export to their Opera Cloud reservations as deposit payments." />

            <ToolkitConfigStep config={config} number={1} ready={ready} />

            <ToolkitFileStep number={2} title="Opera v5 deposit ledger" label="Choose the deposit export (CSV / pipe)"
                accept=".csv,.txt,.rep" kind="deposits" onParsed={batch.load} disabled={!ready}>
                {!!batch.rows.length && missing.length > 0 && <p className="tkb-hint">Posting needs {missing.join(', ')}.</p>}
            </ToolkitFileStep>

            <ToolkitBatchTable batch={batch} columns={COLUMNS} searchKeys={SEARCH_KEYS} exportName="Deposit_Ledger"
                canRun={canPost} onClear={() => batch.load([])} rowActions={ROW_ACTIONS} />
        </div>
    );
}

export default ToolkitDepositLedgerPage;
